/**
 * nafathVerifications repository — read-only access to `public.nafath_verifications`.
 *
 * This is the ONLY place that calls `supabase.from("nafath_verifications")` for
 * owner-identity reads in the staff dashboard. Hooks call this repository; nothing
 * else may read the table directly from hooks or components.
 *
 * The table is keyed by (user_id, national_id, company_id, status = 'COMPLETED').
 * The join key is owned by the caller — either the owner's `user_id` or
 * `national_id`; we require the national_id at minimum (user_id is optional for
 * owners whose auth account hasn't been created yet but who were verified via a
 * shared Nafath session).
 *
 * Multi-tenant isolation: every query filters by company_id.
 *
 * ── Migration state ─────────────────────────────────────────────────────────
 *   Step 1 (done) — Consolidated to this repository file.
 *   Step 2 (done) — Guarded NestJS endpoint `GET /nafath/owner-identity`
 *                   in `apps/api/src/nafath/` (owners.view permission).
 *   Step 3 (HERE) — `getForOwner` flipped to NestJS-first with Supabase
 *                   fallback via `reportNestFallback`.
 *
 * ── Auth split (staff-only flip) ────────────────────────────────────────────
 *   `getForOwner` is called only by staff (admin / manager / employee with
 *   owners.view) reading the "Owner Nafath Identity" modal. This is NOT an
 *   authz-split case — no owner/tenant portal calls this method. The guarded
 *   Nest endpoint requires `owners.view`; owner/tenant self-service paths stay
 *   on Supabase RLS until a dedicated self-service endpoint is added.
 *
 * ── Wire shape ───────────────────────────────────────────────────────────────
 *   The NestJS endpoint returns the same identity subset as the Supabase query
 *   (NafathOwnerIdentity). The schema is validated via `nafathOwnerIdentitySchema`
 *   from `@dallali/shared` at the flip boundary; any drift triggers a
 *   transparent Supabase fallback.
 */
import { isDefinitiveNotFound, reportNestFallback } from "@/lib/nestFallback";
import { nafathOwnerIdentitySchema } from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Tables } from "@/integrations/supabase/types";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";

// Auto-generated type for the full row — avoids `any` in the Supabase fallback.
export type NafathVerificationRow = Tables<"nafath_verifications">;

/** Requester-name enrichment projection (unified-tasks board, fe-tail T9). */
export type NafathNameRow = Pick<
  NafathVerificationRow,
  "national_id" | "arabic_first_name" | "arabic_family_name"
>;

/** Self-identity projection for useNafathIdentity (fe-tail T10). */
export type NafathSelfIdentityRow = Pick<
  NafathVerificationRow,
  | "national_id"
  | "id_type"
  | "dob_hijri"
  | "dob_gregorian"
  | "response_payload"
  | "arabic_first_name"
  | "arabic_father_name"
  | "arabic_grand_father_name"
  | "arabic_family_name"
  | "english_first_name"
  | "english_father_name"
  | "english_grand_father_name"
  | "english_family_name"
  | "gender"
  | "nationality_ar"
  | "nationality_en"
  | "updated_at"
>;

/** Settings NafathIdentityCard projection (fe-tail T10). */
export type NafathSelfNameCardRow = Pick<
  NafathVerificationRow,
  | "arabic_first_name"
  | "arabic_father_name"
  | "arabic_grand_father_name"
  | "arabic_family_name"
  | "english_first_name"
  | "english_father_name"
  | "english_grand_father_name"
  | "english_family_name"
  | "gender"
  | "dob_gregorian"
  | "dob_hijri"
  | "nationality_ar"
  | "nationality_en"
  | "updated_at"
>;

/**
 * Fields we surface in the Owner Nafath Identity modal. A strict subset of the
 * row — we intentionally omit internal/operational columns (oidc_state, trans_id,
 * random_code, request_payload, response_payload, request_id).
 *
 * This type is kept byte-identical between the Nest path and the Supabase path.
 */
export type NafathOwnerIdentity = Pick<
  NafathVerificationRow,
  | "id"
  | "national_id"
  | "id_type"
  | "arabic_first_name"
  | "arabic_father_name"
  | "arabic_grand_father_name"
  | "arabic_family_name"
  | "english_first_name"
  | "english_father_name"
  | "english_grand_father_name"
  | "english_family_name"
  | "gender"
  | "dob_hijri"
  | "dob_gregorian"
  | "nationality_ar"
  | "nationality_en"
  | "nationality_code"
  | "id_expiry_hijri"
  | "id_expiry_gregorian"
  | "id_issue_hijri"
  | "id_issue_gregorian"
  | "id_issue_location_ar"
  | "id_issue_location_en"
  | "iqama_expiry_hijri"
  | "iqama_expiry_gregorian"
  | "updated_at"
>;

// Columns for the Supabase fallback select.
const IDENTITY_COLUMNS = [
  "id",
  "national_id",
  "id_type",
  "arabic_first_name",
  "arabic_father_name",
  "arabic_grand_father_name",
  "arabic_family_name",
  "english_first_name",
  "english_father_name",
  "english_grand_father_name",
  "english_family_name",
  "gender",
  "dob_hijri",
  "dob_gregorian",
  "nationality_ar",
  "nationality_en",
  "nationality_code",
  "id_expiry_hijri",
  "id_expiry_gregorian",
  "id_issue_hijri",
  "id_issue_gregorian",
  "id_issue_location_ar",
  "id_issue_location_en",
  "iqama_expiry_hijri",
  "iqama_expiry_gregorian",
  "updated_at",
].join(", ");

// Bind the slice's Nest client once — shared by all flipped methods below.
const nestRequest = makeNestRequest("nafathVerifications");

// ─── Supabase fallback ────────────────────────────────────────────────────────

/**
 * Supabase fallback for `getForOwner`. Behaviour-identical to the pre-flip
 * implementation — kept as the transparent safety net.
 *
 * The `as any` cast is pre-existing: PostgREST's string-literal select overload
 * narrows the result to `GenericStringError` when the column list is a runtime
 * string. The Nest path (the primary path) replaces the need for this cast when
 * available; the fallback preserves existing behaviour exactly.
 */
async function supabaseGetForOwner(
  nationalId: string,
  companyId: string,
  userId?: string | null,
): Promise<NafathOwnerIdentity | null> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let query = (supabase as any)
    .from("nafath_verifications")
    .select(IDENTITY_COLUMNS)
    .eq("national_id", nationalId)
    .eq("company_id", companyId)
    .eq("status", "COMPLETED");

  if (userId) {
    query = query.eq("user_id", userId);
  }

  const { data, error } = await query
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return (data as NafathOwnerIdentity) ?? null;
}

// ─── Repository ───────────────────────────────────────────────────────────────

/**
 * Max national ids per `.in("national_id", …)` request. Every id travels in the
 * PostgREST query string; ~800 verified owners/tenants in one call made a ~10 KB
 * URL that the edge proxy rejects before PostgREST (the browser then reports it
 * as a CORS error). 100 ids ≈ 1.3 KB per request.
 */
export const NATIONAL_ID_BATCH_SIZE = 100;

/** Max batch requests in flight at once (bounded concurrency, conventions §9). */
export const NATIONAL_ID_BATCH_CONCURRENCY = 4;

/** Unique ids in batches of NATIONAL_ID_BATCH_SIZE. */
function nationalIdBatches(nationalIds: string[]): string[][] {
  const unique = [...new Set(nationalIds)];
  const batches: string[][] = [];
  for (let i = 0; i < unique.length; i += NATIONAL_ID_BATCH_SIZE) {
    batches.push(unique.slice(i, i + NATIONAL_ID_BATCH_SIZE));
  }
  return batches;
}

/** Runs `fetchBatch` over every batch, at most NATIONAL_ID_BATCH_CONCURRENCY at a time. */
async function fetchInBatches<T>(
  nationalIds: string[],
  fetchBatch: (batch: string[]) => Promise<T[]>,
): Promise<T[]> {
  const batches = nationalIdBatches(nationalIds);
  const rows: T[] = [];
  for (let i = 0; i < batches.length; i += NATIONAL_ID_BATCH_CONCURRENCY) {
    const pages = await Promise.all(
      batches.slice(i, i + NATIONAL_ID_BATCH_CONCURRENCY).map(fetchBatch),
    );
    for (const page of pages) rows.push(...page);
  }
  return rows;
}

export const nafathVerificationsRepository = {
  /**
   * Fetch the most-recent COMPLETED Nafath verification for an owner.
   *
   * Primary path: `GET /nafath/owner-identity?nationalId=&userId=` (NestJS,
   * owners.view guarded). Response is parsed through `nafathOwnerIdentitySchema`
   * from `@dallali/shared`.
   *
   * Transparent fallback to Supabase on ANY error from the Nest path:
   *   - network failure / timeout
   *   - non-2xx HTTP status
   *   - schema parse failure (drift safety)
   * Every fallback is reported via `reportNestFallback` (Sentry) so a silently-
   * broken Nest endpoint surfaces as an alertable signal.
   *
   * Nest returns 404 when no COMPLETED record exists or when the nationalId
   * belongs to a different company. The fallback then queries Supabase and returns
   * null if absent there too — preserving the contract (null = not found).
   *
   * Join strategy:
   *   1. Prefer matching by national_id (always populated on verified owners;
   *      reliable even before the owner's auth account is created).
   *   2. Also filter by user_id when available (narrows results on shared-NID
   *      scenarios, though rare in practice).
   *   3. company_id is ALWAYS applied for tenant isolation.
   *
   * Returns null when no COMPLETED record exists.
   */
  async getForOwner(
    nationalId: string,
    companyId: string,
    userId?: string | null,
  ): Promise<NafathOwnerIdentity | null> {
    if (API_URL) {
      try {
        const path = API_ROUTES.nafathVerifications.ownerIdentity(nationalId, userId);
        const raw = await nestRequest<unknown>(path);

        // Nest returns null when no COMPLETED record exists (the service throws
        // 404 which the FE treats as a fallback trigger) — but we also handle
        // the case where the server itself returns null.
        if (raw === null || raw === undefined) {
          return null;
        }

        // Parse and validate — schema drift triggers the Supabase fallback.
        const parsed = nafathOwnerIdentitySchema.parse(raw);
        return parsed as NafathOwnerIdentity;
      } catch (err) {
        // A Nest 404 is the authoritative "no COMPLETED record" answer — return
        // null directly (mirrors listings.getPublicById). The Supabase fallback
        // would also return null, so there is no fallback value to recover. Skipping
        // reportNestFallback stops the monitor-3033444 noise fired on every owner
        // profile view where Nafath isn't completed (Sentry DALLALI-FRONTEND-E1).
        if (isDefinitiveNotFound(err)) {
          return null;
        }
        reportNestFallback("nafathVerifications.getForOwner", err);
        try {
          return await supabaseGetForOwner(nationalId, companyId, userId);
        } catch (supabaseErr) {
          throw new Error(
            `nafathVerifications.getForOwner Supabase fallback failed: ${serializeError(supabaseErr)}`,
          );
        }
      }
    }

    // No API_URL configured (local / Supabase-only env) — use Supabase directly.
    return supabaseGetForOwner(nationalId, companyId, userId);
  },

  /**
   * The COMPLETED verification row for one national id (staff tenant-details
   * enrichment). Company-scoped. KEPT on Supabase — staff-side detail read.
   * NEVER throws: the pre-consolidation site destructured `{ data }` only
   * (the details render without the Nafath block), so an error resolves to
   * null.
   */
  async getCompletedByNationalId(
    nationalId: string,
    companyId: string,
  ): Promise<NafathVerificationRow | null> {
    const { data } = await supabase
      .from("nafath_verifications")
      .select("*")
      .eq("national_id", nationalId)
      .eq("company_id", companyId)
      .eq("status", "COMPLETED")
      .maybeSingle();
    return (data as NafathVerificationRow | null) ?? null;
  },

  /**
   * COMPLETED verification rows for a batch of national ids, fetched in
   * NATIONAL_ID_BATCH_SIZE chunks (owners and tenants list
   * enrichment — getOwnerDob/getOwnerName selectors). Company-scoped. KEPT on
   * Supabase. NEVER throws: the pre-consolidation site destructured `{ data }`
   * only (the list renders without Nafath enrichment), so an error resolves
   * to [].
   */
  async listCompletedByNationalIds(
    nationalIds: string[],
    companyId: string,
  ): Promise<NafathVerificationRow[]> {
    return fetchInBatches(nationalIds, async (batch) => {
      const { data } = await supabase
        .from("nafath_verifications")
        .select("*")
        .in("national_id", batch)
        .eq("company_id", companyId)
        .eq("status", "COMPLETED");
      return (data as NafathVerificationRow[] | null) ?? [];
    });
  },

  /**
   * Arabic name columns of COMPLETED verifications for a batch of national
   * ids (batched, see NATIONAL_ID_BATCH_SIZE) — the requester-name
   * enrichment shared by the unified-tasks board and
   * the service-requests repository. Deliberately NOT company-scoped: a
   * service requester's Nafath verification may pre-date / sit outside the
   * viewing company (identity is global). NEVER throws: both pre-consolidation
   * sites destructured `{ data }` only (the board renders without requester
   * names), so an error resolves to [].
   */
  async listCompletedNamesByNationalIds(
    nationalIds: string[],
  ): Promise<NafathNameRow[]> {
    return fetchInBatches(nationalIds, async (batch) => {
      const { data } = await supabase
        .from("nafath_verifications")
        .select("national_id, arabic_first_name, arabic_family_name")
        .in("national_id", batch)
        .eq("status", "COMPLETED");
      return (data as NafathNameRow[] | null) ?? [];
    });
  },

  /**
   * SELF-scoped: the caller's own latest COMPLETED verification, keyed by
   * user_id (company filter applied only when the profile has one) — the
   * "My Nafath identity" read (useNafathIdentity). Runs as the user's own
   * RLS session, NOT a staff endpoint. NEVER throws: the pre-consolidation
   * site returned null on error (identity block simply hidden).
   *
   * The select string stays single-line — string concatenation breaks
   * PostgREST's type inference and widens the row to GenericStringError.
   */
  async getSelfIdentityRow(
    userId: string,
    companyId?: string | null,
  ): Promise<NafathSelfIdentityRow | null> {
    let query = supabase
      .from("nafath_verifications")
      .select("national_id, id_type, dob_hijri, dob_gregorian, response_payload, arabic_first_name, arabic_father_name, arabic_grand_father_name, arabic_family_name, english_first_name, english_father_name, english_grand_father_name, english_family_name, gender, nationality_ar, nationality_en, updated_at")
      .eq("user_id", userId)
      .eq("status", "COMPLETED")
      .order("updated_at", { ascending: false })
      .limit(1);

    if (companyId) {
      query = query.eq("company_id", companyId);
    }

    const { data, error } = await query.maybeSingle();
    if (error) return null;
    return (data as NafathSelfIdentityRow | null) ?? null;
  },

  /**
   * Staff-side: latest COMPLETED verification carrying an Arabic name for a
   * NATIONAL ID (request-details identity panel — useRequestNafathData).
   * Company-scoped. THROWS on error (the query hook surfaces it), unlike the
   * self-scoped variants below.
   */
  async getCompletedNameCardByNationalId(
    nationalId: string,
    companyId: string,
  ): Promise<NafathSelfNameCardRow | null> {
    const { data, error } = await supabase
      .from("nafath_verifications")
      .select("arabic_first_name, arabic_father_name, arabic_grand_father_name, arabic_family_name, english_first_name, english_father_name, english_grand_father_name, english_family_name, gender, dob_gregorian, dob_hijri, nationality_ar, nationality_en, updated_at")
      .eq("national_id", nationalId)
      .eq("company_id", companyId)
      .eq("status", "COMPLETED")
      .not("arabic_first_name", "is", null)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return (data as NafathSelfNameCardRow | null) ?? null;
  },

  /**
   * SELF-scoped: the caller's own latest COMPLETED verification that carries
   * an Arabic name — the settings NafathIdentityCard read. user_id +
   * company_id keyed, `arabic_first_name IS NOT NULL`. NEVER throws — the
   * pre-consolidation site silently failed (card hidden).
   */
  async getSelfVerifiedNameRow(
    userId: string,
    companyId: string,
  ): Promise<NafathSelfNameCardRow | null> {
    const { data, error } = await supabase
      .from("nafath_verifications")
      .select("arabic_first_name, arabic_father_name, arabic_grand_father_name, arabic_family_name, english_first_name, english_father_name, english_grand_father_name, english_family_name, gender, dob_gregorian, dob_hijri, nationality_ar, nationality_en, updated_at")
      .eq("user_id", userId)
      .eq("company_id", companyId)
      .eq("status", "COMPLETED")
      .not("arabic_first_name", "is", null)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) return null;
    return (data as NafathSelfNameCardRow | null) ?? null;
  },
};
